// Client-side mirror of the window the driver-moves drawer queries.
//
// The drawer (`GET /driver-status-monitor/drivers/{unit}`) defaults start/end to
// TODAY and filters legs with driver_status_monitor.date_range_or_missed_appt_sql:
//   - origapptdate OR destapptdate falls in the range (i.e. == today), OR
//   - an appt date is in the PAST and the leg hasn't departed yet (missed appt,
//     still on the driver's plate).
//
// Reproducing that here lets the driver icons hide the moves link on legs the
// drawer would have nothing to say about — e.g. a completed leg from last week,
// where "View moves" silently jumps to an unrelated day's work.

import { legDateToIso } from './dateUtils';

export interface LegMovesWindow {
  /** Leg origin appt date in display form (MM/DD, MM/DD/YY, MM/DD/YYYY). */
  origApptDate?: string | null;
  /** Leg destination appt date in display form. */
  destApptDate?: string | null;
  /**
   * The leg's destination depart stamp — v1 `legscurrent.departdatetime`,
   * surfaced as `OrderLeg.destDepartDateTime`. Blank/absent means "not
   * departed", which is also the fail-open default for callers (Dispatch
   * Planner, LC cards) whose rows don't carry the stamp.
   */
  departDateTime?: string | null;
}

// Tolerates a trailing time component ('MM/DD/YY HH:MM'); legDateToIso would
// otherwise fold the time into the year segment and land in the wrong century.
function apptDayToIso(value: string | null | undefined): string | null {
  const day = (value ?? '').trim().split(' ')[0];
  return legDateToIso(day);
}

function toIsoDay(now: Date): string {
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

/**
 * Would this leg show up in the driver-moves drawer for today?
 *
 * Both appt dates blank/unparseable ⇒ `true`. An undated leg isn't "scheduled
 * for another day", and the driver may still have other moves today, so the
 * link is left alone rather than removed on a guess.
 */
export function legIsInTodaysMoves(leg: LegMovesWindow, now: Date = new Date()): boolean {
  const orig = apptDayToIso(leg.origApptDate);
  const dest = apptDayToIso(leg.destApptDate);
  if (!orig && !dest) return true;

  const today = toIsoDay(now);
  if (orig === today || dest === today) return true;

  const hasPastAppt = (!!orig && orig < today) || (!!dest && dest < today);
  const departed = (leg.departDateTime ?? '').trim() !== '';
  return hasPastAppt && !departed;
}
